import { translate as t } from '@/lib/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, schedulerApi } from '@/api';
import { accountKeys, analyticsKeys, schedulerKeys } from '@/lib/queryKeys';
import type { TriggerRollupDailyRequest, TriggerRollupHourlyRequest } from '@/types/api';

function handleTriggerError(context: string, contextEn: string) {
  return (err: unknown) => {
    const apiErr = ApiError.from(err);
    if (apiErr.isConflict()) {
      toast.error(t(`${context} impossible`, `Unable to ${contextEn}`), {
        description: t('Un run est déjà en cours.', 'A run is already in progress.'),
      });
    } else if (apiErr.isForbidden()) {
      toast.error(t('Accès refusé', 'Access denied'), {
        description: t('Le rôle ADMIN est requis.', 'The ADMIN role is required.'),
      });
    } else {
      toast.error(t(`${context} impossible`, `Unable to ${contextEn}`), {
        description: apiErr.localizedMessage,
      });
    }
  };
}

export function useTriggerCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: schedulerApi.triggerCycle,
    onSuccess: () => {
      toast.success(t('Cycle principal déclenché', 'Main cycle started'));
      qc.invalidateQueries({ queryKey: schedulerKeys.all });
      qc.invalidateQueries({ queryKey: accountKeys.all });
    },
    onError: handleTriggerError('Déclenchement du cycle', 'start the cycle'),
  });
}

export function useTriggerProfileRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: schedulerApi.triggerProfileRefresh,
    onSuccess: () => {
      toast.success(t('Rafraîchissement des profils déclenché', 'Profile refresh started'));
      qc.invalidateQueries({ queryKey: schedulerKeys.all });
    },
    onError: handleTriggerError('Rafraîchissement des profils', 'refresh profiles'),
  });
}

export function useTriggerRollupHourly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TriggerRollupHourlyRequest = {}) => schedulerApi.triggerRollupHourly(body),
    onSuccess: (result) => {
      toast.success(t('Rollup horaire terminé', 'Hourly rollup complete'), {
        description: t(`Heure : ${result.hour ?? '—'}`, `Hour: ${result.hour ?? '—'}`),
      });
      qc.invalidateQueries({ queryKey: schedulerKeys.all });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
    },
    onError: handleTriggerError('Rollup horaire', 'run hourly rollup'),
  });
}

export function useTriggerRollupDaily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TriggerRollupDailyRequest = {}) => schedulerApi.triggerRollupDaily(body),
    onSuccess: (result) => {
      toast.success(t('Rollup journalier terminé', 'Daily rollup complete'), {
        description: t(`Date : ${result.date ?? '—'}`, `Date: ${result.date ?? '—'}`),
      });
      qc.invalidateQueries({ queryKey: schedulerKeys.all });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
    },
    onError: handleTriggerError('Rollup journalier', 'run daily rollup'),
  });
}

export function useTriggerViralDetection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: schedulerApi.triggerViralDetection,
    onSuccess: (result) => {
      toast.success(t('Détection virale terminée', 'Viral detection complete'), {
        description: t(
          `${result.flagged} reel${result.flagged > 1 ? 's' : ''} flaggé${result.flagged > 1 ? 's' : ''}`,
          `${result.flagged} reel${result.flagged === 1 ? '' : 's'} flagged`,
        ),
      });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
    },
    onError: handleTriggerError('Détection virale', 'detect viral reels'),
  });
}
